import React from "react";
import ExpandableZoomableImage from "components/ExpandableZoomableImage";

type FigureLegend = Record<string, string>;

interface ComparisonImageCardProps {
  /** Source patent figure image (presigned URL). */
  patentImageUrl?: string;
  /** Accused instrumentality / target image (presigned URL from original_image_path). */
  accusedImageUrl?: string;
  /** e.g. "Figure_6a" -> rendered as "Figure. 6a". */
  figureNumber?: string;
  /** Source patent PDF link, shown in the expanded overlay header. */
  patentLink?: string;
  /** Target document link (website / PDF), shown in the expanded overlay header. */
  accusedLink?: string;
  /** Presigned link to the saved PDF snapshot of a website target. */
  accusedSnapshotLink?: string;
  /** Optional legend rows: [{ "10": "headrest" }, ...] */
  figureLegend?: FigureLegend[];
  onChangePatentImage?: () => void;
  onAnnotate?: () => void;
  onReanalyze?: () => void;
}

const CameraIcon = ({ size = 40, stroke = "#98A2B3" }: { size?: number; stroke?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <circle cx="8.5" cy="8.5" r="1.5"></circle>
    <polyline points="21 15 16 10 5 21"></polyline>
  </svg>
);

const NoPreview = () => (
  <div className="w-full h-64 bg-[#F2F4F7] border-2 border-dashed border-[#D0D5DD] rounded-lg flex flex-col items-center justify-center gap-2.5">
    <CameraIcon />
    <span className="text-sm text-[#667085] font-interMedium">No Preview Available</span>
  </div>
);

/**
 * Side-by-side "patent figure | accused instrumentality" comparison, with the
 * analysis intended to be rendered underneath (see MarkdownViewer usage).
 */
const ComparisonImageCard: React.FC<ComparisonImageCardProps> = ({
  patentImageUrl,
  accusedImageUrl,
  figureNumber,
  patentLink,
  accusedLink,
  accusedSnapshotLink,
  figureLegend,
  onChangePatentImage,
  onAnnotate,
  onReanalyze,
}) => {
  return (
    <div className="my-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Patent figure */}
        <figure className="m-0 border border-gray-200 rounded-lg shadow-sm bg-white p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <figcaption className="text-xs font-interSemiBold tracking-wide uppercase text-[#667085]">
              Patent Figure
            </figcaption>
            {onChangePatentImage && (
              <button
                type="button"
                onClick={onChangePatentImage}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-white rounded"
                style={{ backgroundColor: "#1751D0", border: "none", cursor: "pointer" }}
              >
                <CameraIcon size={12} stroke="currentColor" />
                Change Image
              </button>
            )}
          </div>
          <div className="flex-1 bg-[#FAFBFC] rounded-md overflow-hidden">
            {patentImageUrl ? (
              <ExpandableZoomableImage
                url={patentImageUrl}
                alt="Patent Figure"
                link={patentLink}
                linkLabel="View Patent"
              />
            ) : (
              <NoPreview />
            )}
          </div>
          {figureNumber && (
            <p className="mt-3 text-center text-sm text-gray-600 font-interMedium">
              {figureNumber.replace(/_/g, ". ")}
            </p>
          )}
        </figure>

        {/* Accused instrumentality */}
        <figure className="m-0 border border-gray-200 rounded-lg shadow-sm bg-white p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <figcaption className="text-xs font-interSemiBold tracking-wide uppercase text-[#667085]">
              Accused Instrumentality
            </figcaption>
            <div className="flex gap-2">
              {onAnnotate && (
                <button
                  type="button"
                  onClick={onAnnotate}
                  className="edit-image-button flex items-center gap-1 px-3 py-1.5 text-xs text-white rounded"
                  style={{ backgroundColor: "#1751D0", border: "none", cursor: "pointer" }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                  Annotation
                  <span className="loading-spinner"></span>
                </button>
              )}
              {onReanalyze && (
                <button
                  type="button"
                  onClick={onReanalyze}
                  className="reanalyze-button flex items-center gap-1 px-3 py-1.5 text-xs text-white rounded"
                  style={{ backgroundColor: "#1751D0", border: "none", cursor: "pointer" }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                  </svg>
                  ReAnalyze
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 bg-[#FAFBFC] rounded-md overflow-hidden">
            {accusedImageUrl ? (
              <ExpandableZoomableImage
                url={accusedImageUrl}
                alt="Accused Instrumentality"
                link={accusedLink}
                linkLabel="View Target"
                snapshotLink={accusedSnapshotLink}
              />
            ) : (
              <NoPreview />
            )}
          </div>
        </figure>
      </div>

      {/* Figure legend (spans full width under the images) */}
      {figureLegend && figureLegend.length > 0 && (
        <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-[#FAFBFC]">
          <p className="text-xs font-interSemiBold uppercase tracking-wide text-[#667085] mb-2">
            Figure Legend
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            {figureLegend.map((legend, index) => {
              const key = Object.keys(legend)[0];
              const value = legend[key];
              return (
                <div key={index} className="flex items-start">
                  <span className="text-sm font-interMedium text-gray-800 mr-2">{key}.</span>
                  <span className="text-sm text-gray-600">{value}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonImageCard;
